/**
 * Preferences › Voice › Vocabulary (desktop): the words Denker should always
 * get right, newest change first, each with how it was misheard; then names
 * from Memory it suggests. A row opens the word.
 */
import { useState } from "react";
import { VOCABULARY_TEXT_MAX } from "@/api/voice-vocabulary";
import { Button, PocketRow, PocketSection, TextInput } from "@/components/ui";
import { useT } from "@/i18n";
import { settingsVoiceVocabularyMessages } from "@/i18n/messages/settings-voice-vocabulary";
import { commonMessages } from "@/i18n/messages/common";
import {
  isPendingVocabularyId,
  useDictationVocabularyStore,
} from "@/stores/dictation-vocabulary-store";
import { useSettingsNav } from "./settings-navigation";
import { SettingsFieldError } from "./settings-text-field";
import { heardAsSummary } from "./voice-vocabulary-summary";
import { VoiceVocabularySuggestions } from "./voice-vocabulary-suggestions";

export function VoiceVocabularyPage() {
  const nav = useSettingsNav();
  const t = useT(settingsVoiceVocabularyMessages);
  const tCommon = useT(commonMessages);
  const words = useDictationVocabularyStore((state) => state.words);
  const addWord = useDictationVocabularyStore((state) => state.addWord);
  const removeWord = useDictationVocabularyStore((state) => state.removeWord);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const typed = text.trim();

  function add() {
    if (!typed) return;
    setText("");
    setError(null);
    addWord(typed, "user").catch(() => setError(t("addWordFailed")));
  }

  function remove(wordId: string) {
    setError(null);
    removeWord(wordId).catch(() => setError(t("removeWordFailed")));
  }

  return (
    <div data-testid="settings-page-preferences-voice-vocabulary">
      <PocketSection>
        <div className="flex items-center gap-1.5 px-2 pb-1.5">
          <TextInput
            shape="capsule"
            value={text}
            maxLength={VOCABULARY_TEXT_MAX}
            placeholder={t("addWordPlaceholder")}
            aria-label={t("addWordPlaceholder")}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              add();
            }}
            data-testid="voice-vocabulary-input"
          />
          {typed ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={add}
              data-testid="voice-vocabulary-add"
            >
              {t("add")}
            </Button>
          ) : null}
        </div>
        {words.map((word) => (
          <PocketRow
            key={word.id}
            testId={`voice-vocabulary-word-${word.id}`}
            title={word.text}
            secondLine={heardAsSummary(word)}
            chevron
            onSelect={
              isPendingVocabularyId(word.id)
                ? undefined
                : () =>
                    nav.push({
                      page: "preferences-voice-vocabulary-word",
                      args: { wordId: word.id, title: word.text },
                    })
            }
            hoverActions={
              <Button
                variant="secondary"
                size="sm"
                disabled={isPendingVocabularyId(word.id)}
                onClick={() => remove(word.id)}
                data-testid={`voice-vocabulary-remove-${word.id}`}
              >
                {tCommon("remove")}
              </Button>
            }
          />
        ))}
        <p className="p-2 text-appkit-small text-muted">{t("listFootnote")}</p>
        <SettingsFieldError error={error} testId="voice-vocabulary-error" />
      </PocketSection>
      <VoiceVocabularySuggestions />
    </div>
  );
}
